import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { LuFileText, LuInbox, LuSave, LuSparkles, LuUpload, LuUser } from "react-icons/lu";
import { setProfileData, setTechSkills } from "../../store/actions/actions";
import topCountries from "../../jsx/components/Dashboard/Profile/topCountries";
import { Btn, Badge, Field, Skel, Note, initialsOf } from "../components/Ui";
import "./Profile.css";

/*
 * Data behavior mirrors the legacy Profile.js:
 *  - send_resume_details_front_end_v2 (read)  -> filenames + profileData
 *    (fetched 4s after the token is ready, exactly like legacy)
 *  - get_skills_technologies_from_resume_v2   -> techSkills
 *    (payload built exactly like legacy, including its filenames[0].name quirk)
 *  - send_resume_details_front_end_v2 (write) -> { emailid, modify: { columns } }
 *    with only the changed columns, tracked against the initially fetched row.
 */
const FILE_API = "https://us-east1-foursssolutions.cloudfunctions.net/send_resume_details_front_end_v2";
const SKILLS_API = "https://us-east1-foursssolutions.cloudfunctions.net/get_skills_technologies_from_resume_v2";

/* ---------- phone formatting (verbatim from legacy Profile.js) ---------- */
function formatUSPhoneNumber(value) {
  let cleaned = value.trim();
  if (cleaned.startsWith("+1")) {
    cleaned = cleaned.slice(2).trim();
  }
  let x = cleaned.replace(/[^\d]/g, "");
  x = x.substring(0, 10);

  if (!x) return "";
  if (x.length < 4) {
    return "(" + x;
  } else if (x.length < 7) {
    return `(${x.slice(0, 3)}) ${x.slice(3)}`;
  } else {
    return `+1 (${x.slice(0, 3)}) ${x.slice(3, 6)}-${x.slice(6)}`;
  }
}

function formatInternationalPhoneNumber(value, countryCode) {
  const cc = String(countryCode || "").replace(/[^\d]/g, "");
  let digits = String(value || "").replace(/[^\d]/g, "");

  if (cc && digits.startsWith(cc)) {
    digits = digits.slice(cc.length);
  }
  if (cc === "91") {
    digits = digits.substring(0, 10);
  } else {
    digits = digits.substring(0, 15);
  }

  if (!cc && !digits) return "";
  if (!digits) return `+${cc}`;

  let grouped = digits;
  if (cc === "91" && digits.length > 5) {
    grouped = `${digits.slice(0, 5)} ${digits.slice(5)}`;
  }
  return `+${cc} ${grouped}`.trim();
}

function formatPhoneByCountry(value, country, countryCode) {
  const ccRaw = String(countryCode || "").trim();
  const cc = ccRaw.replace(/[^\d]/g, "");
  const isUS =
    cc === "1" ||
    ccRaw === "+1" ||
    String(country || "").toLowerCase() === "united states" ||
    String(country || "").toLowerCase() === "united states of america";
  if (isUS) {
    return formatUSPhoneNumber(value);
  }
  return formatInternationalPhoneNumber(value, cc);
}

const getCallingCode = (countryName) => {
  const country = topCountries.find(
    (c) => c.name.toLowerCase() === String(countryName || "").toLowerCase()
  );
  return country ? country.callingCode : "";
};

const getChangedFields = (original = {}, current = {}) => {
  const changed = {};
  for (const [k, v] of Object.entries(current)) {
    if (original?.[k] !== v) changed[k] = v;
  }
  return changed;
};

export default function Profile() {
  const dispatch = useDispatch();
  const userEmail = useSelector((s) => s.auth.auth.email);
  const subscription = useSelector((s) => s.auth.auth.subscription);
  const {
    profileData,
    techSkills,
    apiToken,
    apiTokenReady,
    subscriptionNextAction,
    fileResume: storeFileResume,
  } = useSelector((s) => s.profile);

  const initialProfileRef = useRef(null);
  const [submitActive, setSubmitActive] = useState(false);
  const [cancelStore, setCancelStore] = useState([]);
  const [filenames, setFilenames] = useState([]);
  /* if the shell already prefetched the profile record, render from it
     immediately instead of showing a spinner while this page re-fetches */
  const hasPrefetchedProfile = profileData && Object.keys(profileData).length > 1;
  const [loading, setLoading] = useState(!hasPrefetchedProfile);
  const [error, setError] = useState(null);
  const [skillsError, setSkillsError] = useState(null);
  const [saveNote, setSaveNote] = useState(null);
  const [saving, setSaving] = useState(false);

  /* --- send_resume_details_front_end_v2 read (same payload/parsing/dispatches, incl. 4s delay) --- */
  useEffect(() => {
    let timer;
    const fetchFilenames = async () => {
      try {
        const queryObj = { emailid: `${userEmail}` };
        const response = await fetch(FILE_API, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiToken}`,
          },
          body: JSON.stringify(queryObj),
        });
        if (!response.ok) {
          throw new Error("Failed to fetch data");
        }
        const data = await response.json();
        const extractedFilenames = data.file_details.map((file) => file.file_name);
        setFilenames(extractedFilenames[0]);

        const extractedProfileData = data.user_details[0];
        initialProfileRef.current = extractedProfileData;
        dispatch(setProfileData(extractedProfileData));
        setCancelStore(extractedProfileData);
        dispatch(setProfileData({ ...extractedProfileData, password: "" }));
        setSubmitActive(true);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    /* seed the save/reset baselines from the prefetched store record so editing
       is safe even before this refresh returns */
    if (hasPrefetchedProfile && !initialProfileRef.current) {
      initialProfileRef.current = profileData;
      setCancelStore(profileData);
      setSubmitActive(true);
    }
    /* fetch immediately (legacy waited 4s before this call — pure dead time) */
    if (userEmail !== "" && apiToken !== "") {
      fetchFilenames();
    }
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiTokenReady]);

  /* --- get_skills_technologies_from_resume_v2 (payload built exactly like legacy) --- */
  const fetchSkillsTechnologies = async () => {
    if (!filenames || filenames?.length === 0) {
      return;
    }
    try {
      setSkillsError(null);
      const fileName = filenames[0].name; // legacy quirk kept: filenames is a string here
      const bodyData = {
        email_id: userEmail,
        file_name: fileName,
        app_name: "frontend",
      };
      const response = await fetch(SKILLS_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify(bodyData),
      });
      if (!response.ok) {
        throw new Error("Failed to fetch skills, technologies, and title");
      }
      const data = await response.json();
      dispatch(setTechSkills(data));
    } catch (err) {
      setSkillsError(err.message);
    }
  };

  useEffect(() => {
    if (filenames?.length > 0) {
      fetchSkillsTechnologies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filenames, dispatch]);

  /* ---------- edit handlers (same field logic as legacy) ---------- */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "mobile_no") {
      /* Format the phone by the chosen country (Muni 2026-08-22). Country-aware,
         so international numbers (e.g. +91) are grouped rather than forced into a
         US shape — which was the original Pavithra Profile #1 concern. */
      const finalCode = profileData.country_code || getCallingCode(profileData.country) || "";
      const formatted = formatPhoneByCountry(value, profileData.country, finalCode);
      dispatch(setProfileData({ ...profileData, country_code: finalCode, mobile_no: formatted }));
    } else {
      dispatch(setProfileData({ ...profileData, [name]: value }));
    }
  };

  const selectCountry = (c) => {
    dispatch(
      setProfileData({
        ...profileData,
        country: c.name || "",
        country_code: c.callingCode || "",
      })
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveNote(null);
    if (!profileData.first_name || !profileData.last_name || !profileData.username) {
      setSaveNote({ tone: "danger", text: "Please fill out all fields." });
      return;
    }
    try {
      setSaving(true);
      const changedColumns = getChangedFields(initialProfileRef.current, {
        first_name: profileData.first_name,
        last_name: profileData.last_name,
        username: profileData.username,
        email: profileData.email,
        country: profileData.country,
        skills: profileData.skills,
        gender: profileData.gender,
        mobile_no: profileData.mobile_no,
        country_code: profileData.country_code,
        middle_name: profileData.middle_name,
        about_me: profileData.about_me,
      });
      const columns = {
        ...changedColumns,
        ...(profileData.password?.trim() ? { password: profileData.password } : {}),
      };

      /* mobile_no is a numeric column — sending "" 400s ("Failed to update data"),
         which blocked saving the Profile when the phone was cleared. Strip to digits
         and drop it when empty, exactly like the Resume page, so a no-phone save
         works and both pages behave the same (Pavithra Profile #2 retest). */
      if ("mobile_no" in columns) {
        const digits = String(columns.mobile_no || "").replace(/\D/g, "");
        if (digits) columns.mobile_no = digits;
        else delete columns.mobile_no;
      }

      if (Object.keys(columns).length === 0) {
        toast.info("No changes to update");
        return;
      }

      const updateObj = { emailid: userEmail, modify: { columns } };
      const response = await fetch(FILE_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify(updateObj),
      });
      if (!response.ok) throw new Error("Failed to update data");

      toast.success("Your Profile has been updated successfully");
      initialProfileRef.current = { ...(initialProfileRef.current || {}), ...columns };
    } catch (err) {
      setSaveNote({ tone: "danger", text: err.message });
    } finally {
      setSaving(false);
    }
  };

  /* ---------- derived display data ---------- */
  const fullName = [profileData?.first_name, profileData?.last_name].filter(Boolean).join(" ");
  /* Pro signal: tolerate subscription from login response (subscription_type)
     or localStorage (subscription), and honor subscriptionNextAction ===
     "Pro User" like the legacy Subscriptions.js did. */
  const isPro =
    subscriptionNextAction === "Pro User" ||
    (subscription && String(subscription).toLowerCase() !== "free");
  const planLabel = subscription
    ? String(subscription).charAt(0).toUpperCase() + String(subscription).slice(1)
    : isPro
    ? "Pro"
    : "Free";
  const resumeName =
    (typeof filenames === "string" && filenames) ||
    (typeof storeFileResume === "string" && storeFileResume) ||
    "";
  const topTech = Array.isArray(techSkills?.top_technologies) ? techSkills.top_technologies : [];
  const topSkills = Array.isArray(techSkills?.top_skills) ? techSkills.top_skills : [];
  const plainSkills = Array.isArray(techSkills?.skills)
    ? techSkills.skills
    : typeof techSkills?.skills === "string" && techSkills.skills
    ? techSkills.skills.split(",").map((s) => s.trim()).filter(Boolean)
    : [];
  const hasAnySkills = topTech.length > 0 || topSkills.length > 0 || plainSkills.length > 0;
  const skillsLoaded = techSkills && Object.keys(techSkills).length > 1;
  const skillsLoading = !skillsLoaded && !skillsError && (loading || filenames?.length > 0);

  const chip = (item, i) => {
    const name = typeof item === "string" ? item : item?.name;
    const prof = typeof item === "object" ? item?.proficiency : undefined;
    if (!name) return null;
    return (
      <span className="pf-skill" key={`${name}-${i}`}>
        {name}
        {prof != null && <span className="pf-skill-n">{prof}%</span>}
      </span>
    );
  };

  return (
    <div className="pf-col stack-4">
      {/* identity card (Lovable: glass tile, mint identity square, display-font name) */}
      <section className="ld-glass pf-tile pf-identity">
        <div className="row" style={{ gap: 16, flexWrap: "wrap" }}>
          <span className="pf-id-avatar">{initialsOf(fullName || userEmail)}</span>
          <div className="grow" style={{ minWidth: 0 }}>
            {loading && !fullName ? (
              <div className="stack-2" style={{ gap: 8 }}>
                <Skel w={180} h={20} />
                <Skel w={220} h={13} />
              </div>
            ) : (
              <>
                <h1 className="t-h2">{fullName || userEmail}</h1>
                {techSkills?.recent_job_title && (
                  <div className="t-sm" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {techSkills.recent_job_title}
                  </div>
                )}
                <div className="t-xs mt-1" style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                  {profileData?.email || userEmail}
                </div>
              </>
            )}
          </div>
          <Badge tone={isPro ? "accent" : "plain"}>{planLabel} plan</Badge>
        </div>
      </section>

      {error && (
        <Note tone="danger">Couldn't load your profile details: {error}</Note>
      )}

      {/* resume card (Lovable base-resume row treatment) */}
      <section className="ld-glass pf-tile pf-right">
        <div className="row-between mb-2">
          <h2 className="t-h3 row" style={{ gap: 8 }}>
            <LuFileText size={16} className="t-accent" /> Resume
          </h2>
          <Btn variant="ghost" size="sm" className="btn-pill" to="/resume-upload">
            <LuUpload size={15} /> {resumeName ? "Replace resume" : "Upload resume"}
          </Btn>
        </div>
        {resumeName ? (
          <div className="pf-file">
            <span className="ld-stat-icon">
              <LuFileText size={18} />
            </span>
            <div className="grow" style={{ minWidth: 0 }}>
              <div
                className="t-sm t-ink"
                style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                title={resumeName}
              >
                {resumeName}
              </div>
              <div className="t-xs">Powers your matches, skills and tailored resumes.</div>
            </div>
            <Badge tone="accent">On file</Badge>
          </div>
        ) : loading ? (
          <Skel h={68} r={18} />
        ) : (
          <div className="ld-empty">
            <span className="ld-empty-icon">
              <LuInbox size={18} />
            </span>
            <div className="t-sm t-ink" style={{ fontWeight: 600, fontFamily: "var(--font-display)" }}>
              No resume on file
            </div>
            <div className="t-xs measure">
              Upload your resume so we can match you to jobs and extract your skills.
            </div>
            <Link to="/resume-upload" className="btn btn-pill btn-mint btn-sm mt-1">
              <LuUpload size={14} /> Upload resume
            </Link>
          </div>
        )}
      </section>

      {/* personal details (Lovable form treatment: label-over-input grid, mint save pill) */}
      <section className="ld-glass pf-tile pf-form">
        <div className="mb-3">
          <h2 className="t-h3 row" style={{ gap: 8 }}>
            <LuUser size={16} className="t-accent" /> Personal details
          </h2>
          <p className="t-xs mt-1">Username and email can't be changed.</p>
        </div>
        {loading ? (
          <div className="grid-2">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div className="stack-2" key={i} style={{ gap: 6 }}>
                <Skel w={90} h={12} />
                <Skel h={42} r={8} />
              </div>
            ))}
          </div>
        ) : (
          <form id="pf-details-form" onSubmit={handleSubmit}>
            <div className="stack-3">
              <div className="grid-2">
                <Field label="First name">
                  <input
                    type="text"
                    className="input"
                    name="first_name"
                    placeholder="Enter name"
                    value={profileData.first_name || ""}
                    onChange={handleInputChange}
                  />
                </Field>
                <Field label="Last name">
                  <input
                    type="text"
                    className="input"
                    name="last_name"
                    placeholder="Last name"
                    value={profileData.last_name || ""}
                    onChange={handleInputChange}
                  />
                </Field>
                <Field label="Username">
                  <input
                    type="text"
                    className="input"
                    name="username"
                    placeholder="User name"
                    value={profileData.username || ""}
                    onChange={handleInputChange}
                    disabled
                  />
                </Field>
                <Field label="Email">
                  <input
                    type="text"
                    className="input"
                    name="email"
                    placeholder="Enter email"
                    value={profileData.email || ""}
                    onChange={handleInputChange}
                    disabled
                  />
                </Field>
                <Field label="Phone number">
                  <input
                    type="text"
                    className="input"
                    name="mobile_no"
                    placeholder="Phone number"
                    value={profileData.mobile_no || ""}
                    onChange={handleInputChange}
                  />
                </Field>
                <Field label="Country">
                  <select
                    className="input"
                    /* match the stored value case-insensitively (it may be saved
                       as "united states") so the dropdown reflects it */
                    value={
                      topCountries.find(
                        (c) => c.name.toLowerCase() === String(profileData.country || "").toLowerCase()
                      )?.name || ""
                    }
                    onChange={(e) => {
                      const c =
                        topCountries.find((x) => x.name === e.target.value) || {
                          name: "",
                          callingCode: "",
                        };
                      selectCountry(c);
                    }}
                  >
                    <option value="">Select country</option>
                    {topCountries.map((c) => (
                      <option key={c.code} value={c.name}>
                        {c.name} {c.callingCode ? `(${c.callingCode})` : ""}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <Field label="Let us know about yourself">
                <textarea
                  className="input"
                  rows={5}
                  name="about_me"
                  value={profileData.about_me || ""}
                  onChange={handleInputChange}
                />
              </Field>

              {saveNote && <Note tone={saveNote.tone}>{saveNote.text}</Note>}
            </div>
          </form>
        )}
      </section>

      {/* grouped skills (Lovable extracted-skills badges, grouped with eyebrows) */}
      <section className="ld-glass pf-tile pf-right">
        <div className="row-between mb-2">
          <h2 className="t-h3 row" style={{ gap: 8 }}>
            <LuSparkles size={16} className="t-accent" /> Extracted skills
          </h2>
          {hasAnySkills && (
            <Badge tone="accent">{topTech.length + topSkills.length + plainSkills.length}</Badge>
          )}
        </div>
        {skillsError ? (
          <Note tone="danger">Couldn't load skills: {skillsError}</Note>
        ) : skillsLoading ? (
          <div className="pf-chips">
            {[72, 96, 60, 88, 66, 78].map((w, i) => (
              <Skel key={i} w={w} h={30} r={999} />
            ))}
          </div>
        ) : hasAnySkills ? (
          <div className="stack-3">
            {topTech.length > 0 && (
              <div>
                <div className="ld-eyebrow mb-1">Top technologies</div>
                <div className="pf-chips">{topTech.map(chip)}</div>
              </div>
            )}
            {topSkills.length > 0 && (
              <div>
                <div className="ld-eyebrow mb-1">Top skills</div>
                <div className="pf-chips">{topSkills.map(chip)}</div>
              </div>
            )}
            {plainSkills.length > 0 && (
              <div>
                <div className="ld-eyebrow mb-1">Other skills</div>
                <div className="pf-chips">{plainSkills.map(chip)}</div>
              </div>
            )}
          </div>
        ) : (
          <div className="ld-empty">
            <span className="ld-empty-icon">
              <LuSparkles size={18} />
            </span>
            <div className="t-sm t-ink" style={{ fontWeight: 600, fontFamily: "var(--font-display)" }}>
              No skills yet
            </div>
            <div className="t-xs measure">
              {resumeName
                ? "We couldn't extract skills from your resume yet — check back shortly."
                : "Upload a resume and we'll pull out your key skills automatically."}
            </div>
            {!resumeName && (
              <Link to="/resume-upload" className="btn btn-ghost btn-pill btn-sm mt-1">
                Upload resume
              </Link>
            )}
          </div>
        )}
      </section>

      {/* action row (Lovable wide pass: flex justify-end lg:col-span-2).
          Submits the personal-details form via the form attribute. */}
      <div className="row pf-actions" style={{ justifyContent: "flex-end", gap: 10 }}>
        <Btn
          variant="ghost"
          className="btn-pill"
          onClick={() => {
            dispatch(setProfileData(cancelStore));
            setSaveNote(null);
          }}
          disabled={!submitActive}
        >
          Reset changes
        </Btn>
        <button
          type="submit"
          form="pf-details-form"
          className="btn btn-pill btn-mint"
          disabled={!submitActive || saving}
        >
          {saving ? (
            <span
              className="spinner"
              style={{
                borderColor: "color-mix(in oklab, var(--accent-ink) 35%, transparent)",
                borderTopColor: "var(--accent-ink)",
              }}
            />
          ) : (
            <LuSave size={15} />
          )}
          {submitActive ? "Save changes" : "Getting details..."}
        </button>
      </div>
    </div>
  );
}
